import type { NormalizeOutput } from 'normalizr';

type EntityTables = Record<string, Record<string, unknown>>;

interface NormalizedPayload {
  entities?: EntityTables;
  result: unknown[];
}

function hasResultIds(payload: unknown): payload is NormalizedPayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'result' in payload &&
    Array.isArray(payload.result) &&
    payload.result.length > 0
  );
}

/**
 * Combines the normalized payloads of a schedule load into one data set.
 * Payloads without result ids (failed, unpermitted or non-normalized
 * responses) are skipped. Entity objects are shared with the payloads, not
 * copied.
 */
export function combineScheduleDatasets(payloads: readonly unknown[]): NormalizeOutput {
  const entities: EntityTables = {};
  const result: unknown[] = [];

  for (const payload of payloads) {
    if (!hasResultIds(payload)) {
      continue;
    }

    for (const [entityType, entitiesById] of Object.entries(payload.entities ?? {})) {
      const existing = entities[entityType];
      entities[entityType] = existing ? { ...existing, ...entitiesById } : entitiesById;
    }

    // Overlaid by index, not concatenated: the LOAD_SCHEDULE_DATASET_SUCCESS
    // reducers keep an existing entity whose id is in `result` and replace the
    // rest, so a concatenated list would change which entities survive a load.
    payload.result.forEach((id, index) => {
      if (id !== undefined || !(index in result)) {
        result[index] = id;
      }
    });
  }

  return { entities, result };
}
